import { IUserSubscription } from '@/common/interface';
import { DAILY_COMMITMENT, ENGLISH_EXPERIENCE, LEARNING_GOAL, LEARNING_STYLE, LEVEL } from '@/features/landing/constants';
import api from './api';
import notificationService from './notificationService';
import axios from 'axios';
import { clearSessionHint, hasSessionHint, markSessionHint, takeLegacyRefreshToken } from './authSession';

const AUTH_REFRESH_URL = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api'}/auth/token/refresh/`;

export interface LoginRequest {
    username: string;
    password: string;
}

export interface SimpleRegisterRequest {
    username: string;
    email: string;
    password: string;
    password_confirm: string;
    first_name: string;
    last_name: string;
}

export interface RegisterRequest {
    username: string;
    email: string;
    password: string;
    password_confirm: string;
    first_name: string;
    last_name: string;
    phone_number?: string;
    date_of_birth?: string;
    preferred_learning_time?: 'morning' | 'afternoon' | 'evening' | 'night';
    native_language?: string;
    learning_language?: string;
    proficiency_level?: 'beginner' | 'elementary' | 'intermediate' | 'upper_intermediate' | 'advanced';
    daily_goal_minutes?: number;
}

export interface ProfileCompletionRequest {
    // Personal Details
    phone_number?: string;
    date_of_birth?: string;
    bio?: string;

    // Learning Preferences
    preferred_learning_time?: 'morning' | 'afternoon' | 'evening' | 'night';
    native_language?: string;
    learning_language?: string;
    proficiency_level?: 'beginner' | 'elementary' | 'intermediate' | 'upper_intermediate' | 'advanced';
    daily_goal_minutes?: number;
    weekly_goal_days?: number;
    learning_goals?: string[];

    // Preferences
    study_reminders?: boolean;
    achievement_notifications?: boolean;
}

export interface User {
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    full_name: string;
    phone_number?: string;
    date_of_birth?: string;
    preferred_learning_time?: string;
    role_name: string;
    is_active: boolean;
    date_joined: string;
    level: number;
    xp_points: number;
    xp_target: number;
    current_streak: number;
    /** Current streak from the Streak table (what /auth/user/ returns; current_streak is not sent). */
    streak?: number;
    longest_streak: number;
    is_premium: boolean;
    subscription?: IUserSubscription | null;
    email_verified: boolean;
    must_change_password?: boolean;
    avatar_url?: string;
    profile?: UserProfile;
}

export interface UserProfile {
    bio: string;
    native_language: string;
    learning_language: string;
    proficiency_level: string;
    target_language: string;
    current_level: LEVEL;
    target_level: LEVEL;
    english_experience: ENGLISH_EXPERIENCE;
    daily_commitment: DAILY_COMMITMENT;
    learning_goal: LEARNING_GOAL;
    learning_style: LEARNING_STYLE;
    notification_preferences: Record<string, any>;
    study_reminders: boolean;
    achievement_notifications: boolean;
    total_study_time: number;
    lessons_completed: number;
    exercises_completed: number;
    words_learned: number;
    learning_path_id?: string;
    streak: number;
    xp_points: number;
    xp_target: number;
    level: number;
    current_streak: number;
    longest_streak: number;
}

export interface AuthResponse {
    refresh: string;
    access: string;
    user: User;
    profile_completed: boolean;
    is_initial_assessment_completed: boolean;
    must_change_password?: boolean;
    message: string;
}

export interface RegisterResponse {
    user: User;
    message: string;
    profile_completed: boolean;
}

export interface ProfileCompletionResponse {
    user: User;
    message: string;
    profile_completed: boolean;
}

export interface ProfileCheckResponse {
    profile_completed: boolean;
    user: User;
}

export interface UserSession {
    id: string;
    user: string;
    session_start: string;
    session_end?: string;
    duration_minutes: number;
    activities_completed: number;
    xp_earned: number;
    session_type: 'lesson' | 'exercise' | 'vocabulary' | 'speaking' | 'mixed';
}

export interface UserStats {
    total_study_time: number;
    lessons_completed: number;
    exercises_completed: number;
    words_learned: number;
    current_streak: number;
    longest_streak: number;
    level: number;
    xp_points: number;
    xp_target: number;
    progress_percentage: number;
    achievements_count: number;
    weekly_activity: Array<{
        date: string;
        minutes: number;
        sessions: number;
    }>;
    recent_sessions: UserSession[];
}

export interface UserPreferences {
    preferred_learning_time: string;
    daily_goal_minutes: number;
    weekly_goal_days: number;
    study_reminders: boolean;
    achievement_notifications: boolean;
    notification_preferences: Record<string, any>;
    learning_goals: string[];
}

export interface UpdateProfileRequest {
    first_name?: string;
    last_name?: string;
    phone_number?: string;
    date_of_birth?: string;
    preferred_learning_time?: string;
    avatar?: File;

    target_level?: LEVEL;
    daily_commitment?: DAILY_COMMITMENT;
    bio?: string;
    learning_goal?: LEARNING_GOAL;
    learning_style?: LEARNING_STYLE;
    study_reminders?: boolean;
    achievement_notifications?: boolean;
}

export interface UpdateUserProfileRequest {
    bio?: string;
    native_language?: string;
    learning_language?: string;
    proficiency_level?: string;
    daily_goal_minutes?: number;
    weekly_goal_days?: number;
    learning_goals?: string[];
    notification_preferences?: Record<string, any>;
    study_reminders?: boolean;
    achievement_notifications?: boolean;
}

export interface UpdateUserRequest {
    first_name?: string;
    last_name?: string;
    phone_number?: string;
}

export interface ChangePasswordRequest {
    old_password: string;
    new_password: string;
    confirm_password: string;
}

export interface ForceChangePasswordRequest {
    old_password: string;
    new_password: string;
    confirm_password: string;
}

export interface PasswordResetRequest {
    email: string;
}

export interface PasswordResetConfirmRequest {
    token: string;
    newPassword: string;
    newPasswordConfirm: string;
}

class AuthService {
    async login(credentials: LoginRequest): Promise<AuthResponse> {
        const response = await api.post<AuthResponse>('/auth/login/', credentials);
        return response.data;
    }

    async initializePushNotifications(): Promise<void> {
        try {
            await notificationService.initializePushNotifications();
        } catch (error) {
            console.warn('Failed to initialize push notifications:', error);
        }
    }

    async register(userData: RegisterRequest): Promise<RegisterResponse> {
        const response = await api.post<RegisterResponse>('/auth/register/full/', userData);
        return response.data;
    }

    async simpleRegister(userData: SimpleRegisterRequest): Promise<RegisterResponse> {
        const response = await api.post<RegisterResponse>('/auth/register/', userData);
        return response.data;
    }

    async updateUser(userData: UpdateUserRequest): Promise<User> {
        const response = await api.put<User>('/auth/user/', userData);
        return response.data;
    }

    async completeProfile(profileData: ProfileCompletionRequest): Promise<ProfileCompletionResponse> {
        const response = await api.post<ProfileCompletionResponse>('/auth/profile/complete/', profileData);
        return response.data;
    }

    async checkProfileCompletion(): Promise<ProfileCheckResponse> {
        const response = await api.get<ProfileCheckResponse>('/auth/profile/check-completion/');
        return response.data;
    }

    async logout(): Promise<void> {
        try {
            // Cleanup notification subscriptions before logout
            await notificationService.cleanup();
        } catch (error) {
            console.warn('Failed to cleanup notifications:', error);
        }

        try {
            // The API blacklists the refresh cookie and clears both auth cookies
            await api.post('/auth/logout/', {});
        } finally {
            clearSessionHint();
        }
    }

    /** Renews the access cookie from the refresh cookie (the API reads it; JS never sees it). */
    async refreshToken(): Promise<void> {
        // Use direct axios call to avoid infinite loop with api interceptor
        await axios.post(
            AUTH_REFRESH_URL,
            {},
            {
                headers: {
                    'Content-Type': 'application/json',
                },
                timeout: 10000, // 10 second timeout for refresh requests
                withCredentials: true,
            },
        );
        markSessionHint();
    }

    /**
     * One-time move of a session an older build kept in localStorage: trade its refresh token for
     * the HttpOnly cookies, then forget it. Without this every learner would be signed out on deploy.
     */
    async migrateLegacyTokens(): Promise<void> {
        const legacyRefresh = takeLegacyRefreshToken();
        if (!legacyRefresh) {
            return;
        }
        try {
            await axios.post(
                AUTH_REFRESH_URL,
                { refresh: legacyRefresh },
                { headers: { 'Content-Type': 'application/json' }, timeout: 10000, withCredentials: true },
            );
            markSessionHint();
        } catch (error) {
            console.warn('Failed to move the saved session to cookies:', error);
        }
    }

    async getCurrentUser(): Promise<User> {
        const response = await api.get<User>('/auth/user/');
        return response.data;
    }

    async updateProfile(profileData: UpdateProfileRequest): Promise<UserProfile> {
        const response = await api.patch<UserProfile>('/auth/profile/', profileData);
        return response.data;
    }

    async updateUserProfile(data: {
        user?: Partial<User>;
        profile?: Partial<UserProfile>;
        avatar?: File;
    }): Promise<{ user: User; profile: UserProfile }> {
        const formData = new FormData();

        // Add user data
        if (data.user) {
            Object.entries(data.user).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    formData.append(key, value.toString());
                }
            });
        }

        // Add profile data
        if (data.profile) {
            Object.entries(data.profile).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    if (Array.isArray(value)) {
                        formData.append(key, JSON.stringify(value));
                    } else {
                        formData.append(key, value.toString());
                    }
                }
            });
        }

        // Add avatar file if present
        if (data.avatar) {
            formData.append('avatar_url', data.avatar);
        }

        const response = await api.patch('/auth/profile/', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    }

    async changePassword(data: {
        old_password: string;
        new_password: string;
        confirm_password: string;
    }): Promise<{ message: string }> {
        const response = await api.put('/auth/change-password/', data);
        return response.data;
    }

    async forceChangePassword(data: ForceChangePasswordRequest): Promise<{ must_change_password: boolean }> {
        const response = await api.post('/auth/force-change-password/', data);
        return response.data;
    }

    async resetPassword(email: string): Promise<{ message: string }> {
        const response = await api.post('/auth/reset-password/', { email });
        return response.data;
    }

    async confirmResetPassword(data: {
        token: string;
        new_password: string;
        new_password_confirm: string;
    }): Promise<{ message: string }> {
        const response = await api.post('/auth/reset-password/confirm/', data);
        return response.data;
    }

    // Session hint (EAA-2090): the tokens are HttpOnly cookies; "signed in" is confirmed by /auth/user/
    markSignedIn(): void {
        markSessionHint();
    }

    /** True when a cookie session may exist (worth asking /auth/user/); not proof of being signed in. */
    hasSession(): boolean {
        return hasSessionHint();
    }

    clearSession(): void {
        clearSessionHint();
    }

    isAuthenticated(): boolean {
        return this.hasSession();
    }

    // User management endpoints
    async getUserProfile(): Promise<UserProfile> {
        const response = await api.get('/auth/profile/');
        return response.data;
    }

    async deleteAccount(password: string): Promise<void> {
        await api.delete('/auth/delete-account/', { data: { password } });
    }

    // Email verification endpoints
    async requestEmailVerification(): Promise<void> {
        await api.post('/auth/email-verification/');
    }

    async verifyEmail(token: string): Promise<void> {
        await api.post('/auth/email-verification/confirm/', { token });
    }

    // User statistics and preferences
    async getUserStats(): Promise<UserStats> {
        const response = await api.get('/auth/stats/');
        return response.data;
    }

    async getUserPreferences(): Promise<UserPreferences> {
        const response = await api.get('/auth/preferences/');
        return response.data;
    }

    async updatePreferences(preferences: Partial<UserPreferences>): Promise<void> {
        await api.put('/auth/preferences/update/', preferences);
    }

    // Learning session endpoints
    async getUserSessions(
        page: number = 1,
        limit: number = 10,
    ): Promise<{
        results: UserSession[];
        count: number;
        next?: string;
        previous?: string;
    }> {
        const response = await api.get(`/auth/sessions/?page=${page}&limit=${limit}`);
        return response.data;
    }

    async getSessionById(sessionId: string): Promise<UserSession> {
        const response = await api.get(`/auth/sessions/${sessionId}/`);
        return response.data;
    }

    async startSession(sessionType: UserSession['session_type'] = 'mixed'): Promise<UserSession> {
        const response = await api.post('/auth/sessions/start/', { session_type: sessionType });
        return response.data;
    }

    async endSession(sessionData: {
        session_id: string;
        duration_minutes: number;
        activities_completed: number;
        xp_earned: number;
    }): Promise<UserSession> {
        const response = await api.post('/auth/sessions/end/', sessionData);
        return response.data;
    }

    async updateSession(sessionId: string, data: Partial<UserSession>): Promise<UserSession> {
        const response = await api.put(`/auth/sessions/${sessionId}/`, data);
        return response.data;
    }

    // File upload methods
    async uploadAvatar(file: File): Promise<{ message: string; avatar_url: string; file_info: any }> {
        const formData = new FormData();
        formData.append('avatar', file);

        const response = await api.post('/auth/upload/avatar/', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    }

    async uploadGeneralFile(
        file: File,
        folder: string = 'general',
        makePublic: boolean = true,
    ): Promise<{ message: string; file_url: string; file_info: any }> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('folder', folder);
        formData.append('make_public', makePublic.toString());

        const response = await api.post('/auth/upload/general/', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    }

    async uploadLessonMaterial(
        file: File,
        lessonId: number,
    ): Promise<{ message: string; file_url: string; file_info: any }> {
        const formData = new FormData();
        formData.append('material', file);

        const response = await api.post(`/auth/upload/lesson/${lessonId}/material/`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    }

    async uploadExerciseMedia(
        file: File,
        exerciseId: number,
    ): Promise<{ message: string; file_url: string; file_info: any }> {
        const formData = new FormData();
        formData.append('media', file);

        const response = await api.post(`/auth/upload/exercise/${exerciseId}/media/`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    }

    async uploadVocabularyAudio(
        file: File,
        vocabularyId: number,
    ): Promise<{ message: string; file_url: string; file_info: any }> {
        const formData = new FormData();
        formData.append('audio', file);

        const response = await api.post(`/auth/upload/vocabulary/${vocabularyId}/audio/`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return response.data;
    }

    async deleteFile(fileKey: string): Promise<{ message: string }> {
        const response = await api.delete('/auth/files/delete/', {
            data: { file_key: fileKey },
        });
        return response.data;
    }

    async getFileUrl(fileKey: string, expiresIn: number = 3600): Promise<{ file_url: string; expires_in: number }> {
        const response = await api.get('/auth/files/url/', {
            params: { file_key: fileKey, expires_in: expiresIn },
        });
        return response.data;
    }

    async checkFileExists(fileKey: string): Promise<{ exists: boolean; file_key: string }> {
        const response = await api.get('/auth/files/exists/', {
            params: { file_key: fileKey },
        });
        return response.data;
    }

    // Initialize notifications for already logged in users
    async initializeNotificationsIfAuthenticated(): Promise<void> {
        if (this.isAuthenticated()) {
            try {
                await notificationService.initializePushNotifications();
            } catch (error) {
                console.warn('Failed to initialize push notifications for authenticated user:', error);
            }
        }
    }

    // Listen for service worker messages
    initializeServiceWorkerListener(): void {
        if ('serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('message', (event) => {
                const { data } = event;

                switch (data.type) {
                    case 'NOTIFICATION_CLICK':
                        // Handle notification click navigation
                        if (data.url) {
                            window.location.href = data.url;
                        }
                        break;

                    case 'MARK_NOTIFICATION_READ':
                        // Mark notification as read
                        if (data.notificationId) {
                            notificationService
                                .markNotificationsAsRead([data.notificationId])
                                .catch((error) => console.error('Failed to mark notification as read:', error));
                        }
                        break;

                    case 'SYNC_NOTIFICATIONS':
                        // Refresh notification stats when sync happens
                        // This would be handled by the component that displays notifications
                        console.log('Notification sync requested');
                        break;

                    default:
                        console.log('Unknown service worker message:', data);
                }
            });
        }
    }
}

export default new AuthService();
